import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  SegmentListMultipleItemMembershipOperator,
  SegmentListSingleItemMembershipOperator,
} from "../checkouts/checkout-tree";
import { ConjunctionMode } from "../segments/segment-types";
import { PostPurchaseSegmentNumberOperator } from "./segment-types";

export enum PostPurchaseNodeType {
  POST_PURCHASE_PAGE = "POST_PURCHASE_PAGE",
  SEGMENT = "SEGMENT",
  SPLIT_TEST = "SPLIT_TEST",
}

// ───────────────────────────────────────────────────────────────────────────────
//   The recursive union of all node-types
// ───────────────────────────────────────────────────────────────────────────────
const postPurchaseNodeSchema = z.lazy(() =>
  z.union([
    postPurchasePageNodeSchema,
    postPurchaseSegmentNodeSchema,
    postPurchaseSplitTestNodeSchema,
  ]),
);
export type PostPurchaseNode = z.infer<typeof postPurchaseNodeSchema>;

const postPurchaseNodeOptionalIdSchema = z.lazy(() =>
  z.union([
    postPurchasePageNodeOptionalIdSchema,
    postPurchaseSegmentNodeOptionalIdSchema,
    postPurchaseSplitTestNodeOptionalIdSchema,
  ]),
);
export type PostPurchaseNodeOptionalId = z.infer<
  typeof postPurchaseNodeOptionalIdSchema
>;

// ───────────────────────────────────────────────────────────────────────────────
//   Restricted nodes for post-purchase and segment matching edges
//   (only POST_PURCHASE_PAGE)
// ───────────────────────────────────────────────────────────────────────────────
const postPurchaseRestrictedNodeSchema = z.lazy(() =>
  z.union([postPurchasePageNodeSchema]),
);
export type PostPurchaseRestrictedNode = z.infer<
  typeof postPurchaseRestrictedNodeSchema
>;
export function isPostPurchaseRestrictedNode(
  node: PostPurchaseNode,
): node is PostPurchaseRestrictedNode {
  switch (node.nodeType) {
    case PostPurchaseNodeType.POST_PURCHASE_PAGE:
      return true;
    case PostPurchaseNodeType.SEGMENT:
    case PostPurchaseNodeType.SPLIT_TEST:
      return false;
    default:
      assertNever(node);
  }
}

const postPurchaseRestrictedNodeOptionalIdSchema = z.lazy(() =>
  z.union([postPurchasePageNodeOptionalIdSchema]),
);

// ───────────────────────────────────────────────────────────────────────────────
//   Post-purchase page edge (recursive, can only point to post-purchase page nodes)
// ───────────────────────────────────────────────────────────────────────────────
const postPurchasePageEdgeSchema_ = z.object({
  childNode: z.lazy(() => postPurchaseRestrictedNodeSchema.optional()),
});
const postPurchasePageEdgeSchema: z.ZodType<PostPurchasePageEdge> =
  postPurchasePageEdgeSchema_;
interface PostPurchasePageEdge
  extends z.infer<typeof postPurchasePageEdgeSchema_> {}

const postPurchasePageEdgeOptionalIdSchema_ = z.object({
  childNode: z.lazy(() =>
    postPurchaseRestrictedNodeOptionalIdSchema.optional(),
  ),
});
const postPurchasePageEdgeOptionalIdSchema: z.ZodType<PostPurchasePageEdgeOptionalId> =
  postPurchasePageEdgeOptionalIdSchema_;
interface PostPurchasePageEdgeOptionalId
  extends z.infer<typeof postPurchasePageEdgeOptionalIdSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   Post-purchase page node
// ───────────────────────────────────────────────────────────────────────────────
const postPurchasePageNodeSchema_ = z.object({
  nodeType: z.literal(PostPurchaseNodeType.POST_PURCHASE_PAGE),
  upsellPageId: zExt.objectId(),
  acceptEdge: postPurchasePageEdgeSchema.optional(),
  declineEdge: postPurchasePageEdgeSchema.optional(),
  _id: zExt.objectId(),
});
const postPurchasePageNodeSchema: z.ZodType<PostPurchasePageNode> =
  postPurchasePageNodeSchema_;
export interface PostPurchasePageNode
  extends z.infer<typeof postPurchasePageNodeSchema_> {}

const postPurchasePageNodeOptionalIdSchema_ = postPurchasePageNodeSchema_
  .partial({ _id: true })
  .omit({ acceptEdge: true, declineEdge: true })
  .extend({
    acceptEdge: postPurchasePageEdgeOptionalIdSchema.optional(),
    declineEdge: postPurchasePageEdgeOptionalIdSchema.optional(),
  });
const postPurchasePageNodeOptionalIdSchema: z.ZodType<PostPurchasePageNodeOptionalId> =
  postPurchasePageNodeOptionalIdSchema_;
interface PostPurchasePageNodeOptionalId
  extends z.infer<typeof postPurchasePageNodeOptionalIdSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   Segmentable characteristics for post-purchase flows
// ───────────────────────────────────────────────────────────────────────────────
export enum SegmentablePostPurchaseCharacteristic {
  ORDER_VALUE = "ORDER_VALUE",
  PRODUCTS_ORDERED = "PRODUCTS_ORDERED",
  PRODUCT_COLLECTIONS = "PRODUCT_COLLECTIONS",
  PRODUCT_METAFIELDS = "PRODUCT_METAFIELDS",
}

// ───────────────────────────────────────────────────────────────────────────────
//   Segment matching edge (can only point to post-purchase page or end nodes)
// ───────────────────────────────────────────────────────────────────────────────
const postPurchaseSegmentMatchingEdgeSchema_ = z.object({
  childNode: postPurchaseRestrictedNodeSchema.optional(),
});
const postPurchaseSegmentMatchingEdgeSchema: z.ZodType<PostPurchaseSegmentMatchingEdge> =
  postPurchaseSegmentMatchingEdgeSchema_;
interface PostPurchaseSegmentMatchingEdge
  extends z.infer<typeof postPurchaseSegmentMatchingEdgeSchema_> {}

const postPurchaseSegmentMatchingEdgeOptionalIdSchema_ = z.object({
  childNode: postPurchaseRestrictedNodeOptionalIdSchema.optional(),
});
const postPurchaseSegmentMatchingEdgeOptionalIdSchema: z.ZodType<PostPurchaseSegmentMatchingEdgeOptionalId> =
  postPurchaseSegmentMatchingEdgeOptionalIdSchema_;
interface PostPurchaseSegmentMatchingEdgeOptionalId
  extends z.infer<typeof postPurchaseSegmentMatchingEdgeOptionalIdSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   Segment non-matching edge (recursive, can point to any node type)
// ───────────────────────────────────────────────────────────────────────────────
const postPurchaseSegmentNonMatchingEdgeSchema_ = z.object({
  childNode: postPurchaseNodeSchema.optional(),
});
const postPurchaseSegmentNonMatchingEdgeSchema: z.ZodType<PostPurchaseSegmentNonMatchingEdge> =
  postPurchaseSegmentNonMatchingEdgeSchema_;
interface PostPurchaseSegmentNonMatchingEdge
  extends z.infer<typeof postPurchaseSegmentNonMatchingEdgeSchema_> {}

const postPurchaseSegmentNonMatchingEdgeOptionalIdSchema_ = z.object({
  childNode: postPurchaseNodeOptionalIdSchema.optional(),
});
const postPurchaseSegmentNonMatchingEdgeOptionalIdSchema: z.ZodType<PostPurchaseSegmentNonMatchingEdgeOptionalId> =
  postPurchaseSegmentNonMatchingEdgeOptionalIdSchema_;
interface PostPurchaseSegmentNonMatchingEdgeOptionalId
  extends z.infer<typeof postPurchaseSegmentNonMatchingEdgeOptionalIdSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   Order value segment
// ───────────────────────────────────────────────────────────────────────────────
const orderValueSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentablePostPurchaseCharacteristic.ORDER_VALUE,
  ),
  amount: z.string(),
  currency: createEnum(Currency),
  operator: createEnum(PostPurchaseSegmentNumberOperator),
});
export const orderValueSegmentSchema: z.ZodType<OrderValueSegment> =
  orderValueSegmentSchema_;
export interface OrderValueSegment
  extends z.infer<typeof orderValueSegmentSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   Products ordered segment
// ───────────────────────────────────────────────────────────────────────────────
const productOrderedSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentablePostPurchaseCharacteristic.PRODUCTS_ORDERED,
  ),
  productIds: z.array(z.string()),
  productListOperator: createEnum(SegmentListSingleItemMembershipOperator),
  cartOperator: createEnum(SegmentListMultipleItemMembershipOperator),
});
export const productOrderedSegmentSchema: z.ZodType<ProductOrderedSegment> =
  productOrderedSegmentSchema_;
export interface ProductOrderedSegment
  extends z.infer<typeof productOrderedSegmentSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   Product collections segment
// ───────────────────────────────────────────────────────────────────────────────
const productCollectionSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentablePostPurchaseCharacteristic.PRODUCT_COLLECTIONS,
  ),
  collectionIds: z.array(z.string()),
  collectionListOperator: createEnum(SegmentListMultipleItemMembershipOperator),
  cartOperator: createEnum(SegmentListMultipleItemMembershipOperator),
});
export const productCollectionSegmentSchema: z.ZodType<ProductCollectionSegment> =
  productCollectionSegmentSchema_;
export interface ProductCollectionSegment
  extends z.infer<typeof productCollectionSegmentSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   Product metafields segment
// ───────────────────────────────────────────────────────────────────────────────
const productMetafieldsSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentablePostPurchaseCharacteristic.PRODUCT_METAFIELDS,
  ),
  metafields: z.array(
    z.object({
      // String literals instead of ShopifyIdType enum — enum values emit as
      // ZodLiteral<any> in .d.ts, which poisons the inferred union type
      resourceType: z.union([
        z.literal("Product" as const),
        z.literal("ProductVariant" as const),
      ]),
      namespace: z.string(),
      key: z.string(),
      value: z.string(),
      valueMatchKind: z
        .union([z.literal("exact"), z.literal("partial")])
        .optional(),
    }),
  ),
  operator: createEnum(SegmentListMultipleItemMembershipOperator),
  cartOperator: createEnum(SegmentListMultipleItemMembershipOperator),
});
export const productMetafieldsSegmentSchema: z.ZodType<ProductMetafieldsSegment> =
  productMetafieldsSegmentSchema_;
export interface ProductMetafieldsSegment
  extends z.infer<typeof productMetafieldsSegmentSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   Post-purchase condition
// ───────────────────────────────────────────────────────────────────────────────
// Use the raw _ schemas in the union so the .d.ts preserves the full
// ZodUnion<[...]> structure instead of ZodType<A | B | C | D> which erases it
const postPurchaseConditionSchema_ = z.union([
  orderValueSegmentSchema_,
  productOrderedSegmentSchema_,
  productCollectionSegmentSchema_,
  productMetafieldsSegmentSchema_,
]);
const postPurchaseConditionSchema: z.ZodType<PostPurchaseCondition> =
  postPurchaseConditionSchema_;
export type PostPurchaseCondition = z.infer<
  typeof postPurchaseConditionSchema_
>;

const postPurchaseConditionBlockSchema = z.object({
  conjunction: createEnum(ConjunctionMode),
  conditions: z.array(postPurchaseConditionSchema),
});
export type PostPurchaseConditionBlock = z.infer<
  typeof postPurchaseConditionBlockSchema
>;

const postPurchaseSegmentSchema = z.object({
  conjunction: createEnum(ConjunctionMode),
  conditionBlocks: z.array(postPurchaseConditionBlockSchema),
});
export type PostPurchaseSegment = z.infer<typeof postPurchaseSegmentSchema>;

// ───────────────────────────────────────────────────────────────────────────────
//   Segment node
// ───────────────────────────────────────────────────────────────────────────────
const postPurchaseSegmentNodeSchema_ = z.object({
  nodeType: z.literal(PostPurchaseNodeType.SEGMENT),
  matchingEdge: postPurchaseSegmentMatchingEdgeSchema,
  nonMatchingEdge: postPurchaseSegmentNonMatchingEdgeSchema,
  segment: postPurchaseSegmentSchema,
  _id: zExt.objectId(),
});
const postPurchaseSegmentNodeSchema: z.ZodType<PostPurchaseSegmentNode> =
  postPurchaseSegmentNodeSchema_;
export interface PostPurchaseSegmentNode
  extends z.infer<typeof postPurchaseSegmentNodeSchema_> {}

const postPurchaseSegmentNodeOptionalIdSchema_ = postPurchaseSegmentNodeSchema_
  .partial({ _id: true })
  .omit({ matchingEdge: true, nonMatchingEdge: true })
  .extend({
    matchingEdge: postPurchaseSegmentMatchingEdgeOptionalIdSchema,
    nonMatchingEdge: postPurchaseSegmentNonMatchingEdgeOptionalIdSchema,
  });
const postPurchaseSegmentNodeOptionalIdSchema: z.ZodType<PostPurchaseSegmentNodeOptionalId> =
  postPurchaseSegmentNodeOptionalIdSchema_;
interface PostPurchaseSegmentNodeOptionalId
  extends z.infer<typeof postPurchaseSegmentNodeOptionalIdSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   SplitTest edge (recursive)
// ───────────────────────────────────────────────────────────────────────────────
const postPurchaseSplitTestEdgeSchema_ = z.object({
  childNode: postPurchaseNodeSchema,
  treatmentId: zExt.objectId(),
});
const postPurchaseSplitTestEdgeSchema: z.ZodType<PostPurchaseSplitTestEdge> =
  postPurchaseSplitTestEdgeSchema_;
export interface PostPurchaseSplitTestEdge
  extends z.infer<typeof postPurchaseSplitTestEdgeSchema_> {}

const postPurchaseSplitTestEdgeOptionalIdSchema_ = z.object({
  childNode: postPurchaseNodeOptionalIdSchema,
  treatmentId: zExt.objectId(),
});
const postPurchaseSplitTestEdgeOptionalIdSchema: z.ZodType<PostPurchaseSplitTestEdgeOptionalId> =
  postPurchaseSplitTestEdgeOptionalIdSchema_;
interface PostPurchaseSplitTestEdgeOptionalId
  extends z.infer<typeof postPurchaseSplitTestEdgeOptionalIdSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   SplitTest node
// ───────────────────────────────────────────────────────────────────────────────
const postPurchaseSplitTestNodeSchema_ = z.object({
  nodeType: z.literal(PostPurchaseNodeType.SPLIT_TEST),
  splitTestId: zExt.objectId(),
  edges: z.array(postPurchaseSplitTestEdgeSchema),
  _id: zExt.objectId(),
});
const postPurchaseSplitTestNodeSchema: z.ZodType<PostPurchaseSplitTestNode> =
  postPurchaseSplitTestNodeSchema_;
export interface PostPurchaseSplitTestNode
  extends z.infer<typeof postPurchaseSplitTestNodeSchema_> {}

const postPurchaseSplitTestNodeOptionalIdSchema_ =
  postPurchaseSplitTestNodeSchema_
    .partial({ _id: true })
    .omit({ edges: true })
    .extend({ edges: z.array(postPurchaseSplitTestEdgeOptionalIdSchema) });
const postPurchaseSplitTestNodeOptionalIdSchema: z.ZodType<PostPurchaseSplitTestNodeOptionalId> =
  postPurchaseSplitTestNodeOptionalIdSchema_;
interface PostPurchaseSplitTestNodeOptionalId
  extends z.infer<typeof postPurchaseSplitTestNodeOptionalIdSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   Root flow + parsing helpers
// ───────────────────────────────────────────────────────────────────────────────
const postPurchaseFlowSchema_ = z.object({ rootNode: postPurchaseNodeSchema });
export const postPurchaseFlowSchema: z.ZodType<PostPurchaseFlow> =
  postPurchaseFlowSchema_;
export interface PostPurchaseFlow
  extends z.infer<typeof postPurchaseFlowSchema_> {}

const postPurchaseFlowOptionalIdsSchema_ = z.object({
  rootNode: postPurchaseNodeOptionalIdSchema,
});
const postPurchaseFlowOptionalIdsSchema: z.ZodType<PostPurchaseFlowOptionalIds> =
  postPurchaseFlowOptionalIdsSchema_;
export interface PostPurchaseFlowOptionalIds
  extends z.infer<typeof postPurchaseFlowOptionalIdsSchema_> {}

export function parsePostPurchaseFlow(
  postPurchaseFlow: unknown,
): PostPurchaseFlow {
  return postPurchaseFlowSchema.parse(postPurchaseFlow);
}

export function safeParsePostPurchaseFlow(
  postPurchaseFlow: unknown,
):
  | { success: true; value: PostPurchaseFlow }
  | { success: false; error: Error } {
  const parsed = postPurchaseFlowSchema.safeParse(postPurchaseFlow);
  if (!parsed.success) {
    return { success: false, error: parsed.error };
  }
  return { success: true, value: parsed.data };
}

export function parsePostPurchaseFlowOptionalIds(
  postPurchaseFlowOptionalIds: unknown,
): PostPurchaseFlowOptionalIds {
  return postPurchaseFlowOptionalIdsSchema.parse(postPurchaseFlowOptionalIds);
}

export function safeParsePostPurchaseFlowOptionalIds(
  postPurchaseFlowOptionalIds: unknown,
):
  | { success: true; value: PostPurchaseFlowOptionalIds }
  | { success: false; error: Error } {
  const parsed = postPurchaseFlowOptionalIdsSchema.safeParse(
    postPurchaseFlowOptionalIds,
  );
  if (!parsed.success) {
    return { success: false, error: parsed.error };
  }
  return { success: true, value: parsed.data };
}
